import React, { createContext, useContext, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { authService } from '../../services/authService'
import onboardingNavigationService from '../../services/onboardingNavigationService'
import { LoadingScreen } from '../ui/LoadingScreen'
import type { User } from '../../types'
import toast from 'react-hot-toast'

interface AuthContextType {
  user: User | null
  isAuthenticated: boolean
  isLoading: boolean
  isLoggingIn: boolean
  login: (email: string, password: string) => Promise<void>
  logout: () => void
  refreshAuth: () => void
  mustChangePassword?: boolean
  recoveryRoute?: string | null
  acceptTerms: () => Promise<boolean>
  acceptPrivacy: () => Promise<boolean>
  buroFailedCounter: number
  updateBuroFailedCounter: (value: number) => void
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth debe ser usado dentro de un AuthProvider')
  }
  return context
}

interface AuthProviderProps {
  children: React.ReactNode
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const navigate = useNavigate()
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isInitialized, setIsInitialized] = useState(false)
  const [mustChangePassword, setMustChangePassword] = useState<boolean>(() =>
    authService.getMustChangePassword()
  )
  const [buroFailedCounter, setBuroFailedCounter] = useState<number>(0)
  const [recoveryRoute, setRecoveryRoute] = useState<string | null>(null)
  const [isLoggingIn, setIsLoggingIn] = useState(false)

  const isAuthenticated = !!user && authService.isAuthenticated()

  // Cargar datos del usuario al inicializar - mejorada la sincronización
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        console.log('🔄 Initializing authentication...')
        // Simular un pequeño delay para asegurar que todo esté cargado
        await new Promise(resolve => setTimeout(resolve, 100))

        if (authService.isAuthenticated()) {
          const userData = authService.getUser()
          if (userData) {
            console.log('👤 User data found, setting user...', userData)
            setUser(userData)
            setMustChangePassword(authService.getMustChangePassword())

            // PRIORIDAD 1: mustChangePassword = true (funcionalidad existente)
            // Si mustChangePassword es true, dejar que CreatePasswordRoute maneje la navegación
            const mustChangePassword = authService.getMustChangePassword()
            if (mustChangePassword) {
              console.log(
                '🔑 mustChangePassword detected on reload, password change flow takes priority'
              )
              console.log(
                '📍 CreatePasswordRoute will handle navigation to /create-password'
              )
              return // Salir temprano - CreatePasswordRoute maneja esto
            }

            // PRIORIDAD 2: Usuario sin credit-application debe ir a pending-proposals
            console.log('🔍 Checking for pending-proposals restriction...')
            try {
              const shouldBeRestricted =
                await authService.shouldBeRestrictedToPendingProposals()
              if (shouldBeRestricted) {
                console.log('🚫 User must be restricted to pending-proposals')
                console.log(
                  '📍 Forcing navigation to /onboarding/pending-proposals'
                )
                navigate('/onboarding/pending-proposals', { replace: true })
                return // Salir temprano para evitar otras verificaciones
              }
            } catch (error) {
              console.error(
                '❌ Error checking pending-proposals restriction:',
                error
              )
            }

            // PRIORIDAD 3: Verificar progressURL normal
            console.log('🔍 Checking for progress URL on page reload...')
            try {
              const progressURL =
                await authService.getCreditApplicationProgress()
              if (progressURL) {
                console.log(
                  '🎯 Progress URL found on reload, redirecting to:',
                  progressURL
                )
                navigate(progressURL, { replace: true })
                return // Salir temprano para evitar otras verificaciones
              } else {
                console.log(
                  '📝 No progress URL found on reload, user can navigate freely'
                )
              }
            } catch (error) {
              console.error('❌ Error checking progress URL on reload:', error)
            }

            // Obtener ruta de recuperación para el onboarding (solo si no hay progressURL)
            try {
              const route = await onboardingNavigationService.getRecoveryRoute()
              setRecoveryRoute(route)
            } catch (error) {
              console.log('No hay flujo de onboarding activo', error)
            }
          } else {
            // Si no hay datos de usuario pero hay token, limpiar
            console.log('⚠️ Token exists but no user data, cleaning up...')
            authService.logout()
          }
        } else {
          console.log('🔓 User not authenticated')
        }
      } catch (error) {
        console.error('Error al inicializar autenticación:', error)
        authService.logout()
        setUser(null)
      } finally {
        setIsLoading(false)
        setIsInitialized(true)
      }
    }

    initializeAuth()
  }, [])

  // Verificar estado del token periódicamente
  useEffect(() => {
    if (!isAuthenticated || !isInitialized) return

    const interval = setInterval(() => {
      authService.refreshTokenIfNeeded()
    }, 60000) // Verificar cada minuto

    return () => clearInterval(interval)
  }, [isAuthenticated, isInitialized])

  const login = async (email: string, password: string): Promise<void> => {
    // isLoggingIn (a diferencia de isLoading, que solo refleja la
    // inicialización de la app) bloquea el useEffect de redirección de
    // LoginPage mientras esta función decide y ejecuta su propia
    // navegación — evita que ambos compitan y el usuario vea un flash a
    // /dashboard antes de terminar en /onboarding/pending-proposals o en
    // el progressURL correspondiente.
    setIsLoggingIn(true)
    try {
      console.log('🔐 Starting login process...')
      const response = await authService.login({ email, password })
      setUser(response.user)
      setMustChangePassword(!!response.mustChangePassword)

      // Verificar que el personType se guardó correctamente
      const savedPersonType = authService.getUserPersonType()
      console.log('🏷️ PersonType saved after login:', savedPersonType)

      console.log('✅ Login successful, checking navigation requirements...')

      // PRIORIDAD 1: mustChangePassword = true (funcionalidad existente)
      // Si mustChangePassword es true, dejar que CreatePasswordRoute maneje la navegación
      if (response.mustChangePassword) {
        console.log(
          '� mustChangePassword detected, password change flow takes priority'
        )
        console.log(
          '📍 CreatePasswordRoute will handle navigation to /create-password'
        )
        return // Salir temprano - CreatePasswordRoute maneja esto
      }

      // PRIORIDAD 2: Usuario sin credit-application debe ir a pending-proposals
      console.log('🔍 Checking pending-proposals restriction...')
      try {
        const shouldBeRestricted =
          await authService.shouldBeRestrictedToPendingProposals()
        if (shouldBeRestricted) {
          console.log(
            '🚫 User must be restricted to pending-proposals after login'
          )
          console.log('📍 Redirecting to /onboarding/pending-proposals')
          navigate('/onboarding/pending-proposals', { replace: true })
          return // Salir temprano para evitar otras redirecciones
        }
      } catch (error) {
        console.error('❌ Error checking pending-proposals restriction:', error)
      }

      // PRIORIDAD 3: Verificar progressURL normal
      try {
        const progressURL = await authService.getCreditApplicationProgress()
        if (progressURL) {
          console.log('🎯 Progress URL found, redirecting to:', progressURL)
          navigate(progressURL, { replace: true })
          return // Salir temprano para evitar otras redirecciones
        } else {
          console.log('📝 No progress URL found, user can navigate freely')
        }
      } catch (error) {
        console.error('❌ Error checking progress URL:', error)
      }

      // Obtener ruta de recuperación después del login (solo si no hay progressURL)
      try {
        const route = await onboardingNavigationService.getRecoveryRoute()
        setRecoveryRoute(route)
      } catch (error) {
        console.log('No hay flujo de onboarding activo', error)
      }
    } finally {
      setIsLoggingIn(false)
    }
  }

  const logout = () => {
    authService.logout()
    authService.clearActiveProgress() // Clear active progress state on logout
    setUser(null)
    setMustChangePassword(false)
  }

  const refreshAuth = () => {
    if (authService.isAuthenticated()) {
      const userData = authService.getUser()
      setUser(userData)
      setMustChangePassword(authService.getMustChangePassword())
    } else {
      setUser(null)
      setMustChangePassword(false)
    }
  }

  const acceptPrivacy = async () => {
    const response = await authService.acceptTermsPrivacy('privacy')
    if (response.success) {
      setUser(
        prevUser =>
          ({
            ...prevUser,
            requiresPrivacyAcceptance: false,
          }) as User
      )
      authService.setUser({ ...user, requiresPrivacyAcceptance: false } as User) // Actualizar en localStorage
      toast.success('Aviso de privacidad aceptado')
      return true
    } else {
      toast.error('Error al aceptar el aviso de privacidad')
      return false
    }
  }

  const acceptTerms = async () => {
    const response = await authService.acceptTermsPrivacy('terms')
    if (response.success) {
      setUser(
        prevUser =>
          ({
            ...prevUser,
            requiresTermsAcceptance: false,
          }) as User
      )
      authService.setUser({ ...user, requiresTermsAcceptance: false } as User) // Actualizar en localStorage
      toast.success('Términos y condiciones aceptados')
      return true
    } else {
      toast.error('Error al aceptar los Términos y Condiciones')
      return false
    }
  }

  const updateBuroFailedCounter = (value: number) => {
    setBuroFailedCounter(value)
  }

  const value: AuthContextType = {
    user,
    isAuthenticated,
    isLoading: isLoading || !isInitialized, // Incluir isInitialized en el loading
    isLoggingIn,
    login,
    logout,
    refreshAuth,
    mustChangePassword,
    recoveryRoute,
    acceptTerms,
    acceptPrivacy,
    buroFailedCounter,
    updateBuroFailedCounter,
  }

  // No renderizar children hasta que esté inicializado
  if (!isInitialized) {
    return <LoadingScreen message="Inicializando aplicación..." />
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export default AuthContext
